<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="notice-container">
    <div
      v-for="notice in notices"
      :key="notice.id"
      :class="['notice-item', { 'expanded': expandedStates[notice.id] }]"
    >
      <div
        :class="['notice-header', { 'not-toggleable': !canToggle(notice) }]"
        @click="toggleNotice(notice.id)"
      >
        <div>
          <!-- Meta information for the special notice -->
          <div
            v-if="isNoticeSpecial(notice)"
          >
            <div class="notice-meta">
              <span class="label">
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="16"
                />
                OFFICIAL NOTICE - {{ formatDate(notice.banner_data.expiray_date) }}
              </span>
              <span class="new-tag">NEW</span>
            </div>
            <div class="special-subject">
              {{ notice.subject }}
            </div>
          </div>
          <div v-else>
            <div class="subject">
              {{ notice.subject }}
            </div>
          </div>
        </div>
        <!-- Toggle icon, hidden for non-toggleable notices -->
        <span
          v-if="canToggle(notice)"
          class="notice-toggle-icon"
        >
          <feather-icon
            icon="ChevronDownIcon"
            size="20"
          />
        </span>
      </div>
      <!-- Notice content, shown when expanded -->
      <div
        v-show="expandedStates[notice.id]"
        class="notice-content"
      >
        <div v-html="notice.notification_body" />
      </div>
    </div>
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessage from '@core/mixins/ErrorMessage'

const bannerRepository = RepositoryFactory.get('banner')

export default {
  mixins: [ErrorMessage],
  data() {
    return {
      notices: [],
      expandedStates: {},
      hasSpecialNotice: false,
    }
  },
  async mounted() {
    await this.fetchLatestBannerData()
    this.initializeNoticeStates()
  },
  // Removed the 'watch' for initialNotices since it's no longer a prop
  methods: {
    async fetchLatestBannerData() {
      try {
        const response = await bannerRepository.getLatestBannerAuth()
        this.notices = response.data.data || []
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    initializeNoticeStates() {
      const now = new Date()

      // First pass: Determine if any "special" notice exists
      this.hasSpecialNotice = this.notices.some(notice => this.isNoticeSpecialCondition(notice, now))

      // Second pass: Initialize expanded states for all notices
      const newExpandedStates = {}
      this.notices.forEach(notice => {
        if (this.hasSpecialNotice) {
          // If a special notice exists, check if THIS notice is the special one
          newExpandedStates[notice.id] = this.isNoticeSpecialCondition(notice, now)
        } else {
          // If no special notice exists, all start collapsed
          newExpandedStates[notice.id] = false
        }
      })
      this.expandedStates = newExpandedStates
    },

    // Helper method to check the special notice condition
    isNoticeSpecialCondition(notice, now) {
      return (
        notice.is_active
        && notice.is_banner_alert_active
        && notice.banner_data
        && notice.banner_data.expiray_date
        && new Date(notice.banner_data.expiray_date) >= now
      )
    },

    // Checks if a notice is the "special" one (active, non-expired, banner-enabled)
    isNoticeSpecial(notice) {
      // Re-evaluate 'now' each time for accurate check (e.g., if page open across midnight)
      return this.isNoticeSpecialCondition(notice, new Date())
    },

    // Determines if a notice can be toggled (expanded/collapsed by user)
    canToggle(notice) {
      // A notice cannot be toggled if there's a special notice AND this particular notice is the special one
      return !(this.hasSpecialNotice && this.isNoticeSpecial(notice))
    },

    // Toggles the expanded state of a notice if it's toggleable
    toggleNotice(noticeId) {
      const notice = this.notices.find(n => n.id === noticeId)
      if (notice && this.canToggle(notice)) {
        this.$set(this.expandedStates, noticeId, !this.expandedStates[noticeId])
      }
    },

    // Formats a date string for display
    formatDate(dateString) {
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}.${month}.${day}`
    },
  },
}
</script>

<style scoped>
/*
  The scoped attribute ensures these styles only apply to this component.
  You'll need to include the CSS from the previous response here.
*/

.notice-item {
    border: 1px solid #e0e6ed;
    border-radius: 15px;
    margin-bottom: 15px;
    overflow: hidden;
    background-color: #fff;
    transition: box-shadow 0.2s ease-in-out;
}

.notice-item.expanded {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.notice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    cursor: pointer;
    background-color: #fcfcfc;
}

.notice-header.not-toggleable {
    cursor: default;
    background-color: #f9f9f9;
}

.notice-toggle-icon {
    transition: transform 0.2s ease-in-out;
    color: #000000;
}

.notice-item.expanded .notice-toggle-icon {
    transform: rotate(180deg);
}

.notice-content {
    padding: 0 20px;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-in-out, padding 0.3s ease-in-out;
}

/* Use a larger max-height for expanded content in Vue 2 due to v-show/v-if timing */
.notice-item.expanded .notice-content {
    max-height: 500px; /* Adjust as needed for content height. Should be larger than actual content. */
    padding: 0 20px 10px 20px;
    color: #524438;
    font-weight: 500;
    text-align: justify;
}

.notice-content p {
    margin: 0 0 10px 0;
    line-height: 1.6;
}
.notice-content p:last-child {
    margin-bottom: 0;
}

.notice-meta {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.notice-meta .label {
    background-color: #8B00001A;
    color: #D8372C;
    padding: 4px 8px;
    border-radius: 4px;
    margin-right: 10px;
}

.notice-meta .new-tag {
    background-color: #8B00001A;
    color: #D8372C;
    padding: 4px 8px;
    border-radius: 4px;
}

.special-subject {
  font-size: 1.5rem;
  color: #444444;
  font-weight: 600;
}

.subject {
  font-size: 1rem;
  color: #444444;
  font-weight: 600;
}
</style>
